import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { commentApi, documentApi, legalApi, qaApi, securityApi, workspaceApi } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import './QAPage.css';

const LEGACY_TODO_TEMPLATES = new Set([
  'verify answer evidence for:',
  'call main contact and confirm latest status',
  'capture next action owner and deadline',
]);

const LEGAL_DOMAIN_OPTIONS = [
  { id: 'EMPLOYMENT', label: 'Arbeidsrecht' },
  { id: 'TRAFFIC', label: 'Verkeersrecht' },
  { id: 'CRIMINAL', label: 'Strafrecht' },
  { id: 'ADMIN', label: 'Bestuursrecht' },
  { id: 'WORKS_COUNCIL', label: 'Medezeggenschap (OR)' },
];

const LEGAL_DOMAIN_LABELS = Object.fromEntries(
  LEGAL_DOMAIN_OPTIONS.map((option) => [option.id, option.label]),
);

function readStoredLegalDomains(workspaceId) {
  if (!workspaceId) return [];
  try {
    const raw = window.localStorage.getItem(`ventus:legal-domains:${workspaceId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const allowed = new Set(LEGAL_DOMAIN_OPTIONS.map((option) => option.id));
    return parsed.filter((item) => allowed.has(item));
  } catch {
    return [];
  }
}

function cleanTodoSuggestionText(value) {
  let text = String(value || '').trim();
  if (!text) return '';

  text = text
    .replace(/^[-*\d.)\s]+/, '')
    .replace(/^"+|"+,?$/g, '')
    .replace(/^'+|'+,?$/g, '')
    .trim();

  const lower = text.toLowerCase();
  if (
    !text ||
    lower === 'items' ||
    lower === '"items"' ||
    lower === '[' ||
    lower === ']' ||
    lower === '{' ||
    lower === '}' ||
    lower === '"items":[' ||
    /^"?items"?\s*:\s*\[?$/i.test(text)
  ) {
    return '';
  }

  return text.replace(/,+$/, '').trim();
}

function normalizeTodoSuggestions(raw) {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.items)) return parsed.items;
    } catch {
      return trimmed.split(/\r?\n/).filter(Boolean);
    }

    return [];
  }

  if (raw && Array.isArray(raw.items)) {
    return raw.items;
  }

  return [];
}

function statusBadgeClass(status) {
  if (status === 'ANSWERED') return 'v-badge v-badge--success';
  if (status === 'FAILED') return 'v-badge v-badge--error';
  return 'v-badge v-badge--warning';
}

function hasMockMarker(value) {
  return /\[mock\]|\bmock[-_\s]?\d|\bmock\b/i.test(String(value || ''));
}

export default function QAPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const prevSyncedQuestionIdRef = useRef(null);

  const [questions, setQuestions] = useState([]);
  const [workspaceRole, setWorkspaceRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [selectedQ, setSelectedQ] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef({});
  const [agendaItems, setAgendaItems] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});
  const [imagePreviewFailedIds, setImagePreviewFailedIds] = useState({});
  const [imagePreviewErrorById, setImagePreviewErrorById] = useState({});
  const [imagePreviewAttemptCounts, setImagePreviewAttemptCounts] = useState({});
  const [openImageDocId, setOpenImageDocId] = useState(null);
  const [caseForm, setCaseForm] = useState({
    location: '',
    contactPerson: '',
    phone: '',
    email: '',
    notes: '',
    driveFrom: '',
    driveTo: '',
  });
  const [todoItems, setTodoItems] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [todoSuggesting, setTodoSuggesting] = useState(false);
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [agendaDraft, setAgendaDraft] = useState({
    title: '',
    objective: '',
    agendaPoints: '',
    decisionsNeeded: '',
    dueAt: '',
  });
  const [agendaBusy, setAgendaBusy] = useState(false);
  const [agendaError, setAgendaError] = useState('');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportTitle, setReportTitle] = useState('Investigation report');
  const [reportQuestionIds, setReportQuestionIds] = useState([]);
  const [reportDocumentIds, setReportDocumentIds] = useState([]);
  const [focusMode, setFocusMode] = useState(false);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [showHeaderStats, setShowHeaderStats] = useState(false);
  const [legalResearching, setLegalResearching] = useState(false);
  const [legalResearchError, setLegalResearchError] = useState('');
  const [legalResearchResult, setLegalResearchResult] = useState(null);
  const [legalSearchQuery, setLegalSearchQuery] = useState('');
  const [legalCaseSearchQuery, setLegalCaseSearchQuery] = useState('');
  const [legalDomains, setLegalDomains] = useState(() => readStoredLegalDomains(workspaceId));
  const [legalBootstrapReady, setLegalBootstrapReady] = useState(false);
  const [legalDetailsOpen, setLegalDetailsOpen] = useState(false);
  const [legalPanelOpen, setLegalPanelOpen] = useState(true);
  const [legalArticlesOpen, setLegalArticlesOpen] = useState(true);
  const [legalCasesOpen, setLegalCasesOpen] = useState(false);
  const [legalAssistantOpen, setLegalAssistantOpen] = useState(true);

  const legalCitations = useMemo(() => {
    const items = Array.isArray(legalResearchResult?.citations) ? legalResearchResult.citations : [];
    return items.filter((cite) => !hasMockMarker(`${cite?.title || ''} ${cite?.quotedText || ''} ${cite?.sourceId || ''}`));
  }, [legalResearchResult]);

  const legalSourceCount = legalCitations.length;
  const legalMatchedChunks = legalCitations.filter((cite) => String(cite?.sourceType || '').toUpperCase() === 'DOCUMENT');
  const legalArticleCitations = legalCitations.filter((cite) => String(cite?.sourceType || '').toUpperCase() === 'LAW');
  const legalCaseCitations = legalCitations.filter((cite) => String(cite?.sourceType || '').toUpperCase() === 'CASE');
  const legalRelatedArticles = useMemo(() => {
    const items = Array.isArray(legalResearchResult?.relatedLaws) ? legalResearchResult.relatedLaws : [];
    return items.filter((item) => !hasMockMarker(item));
  }, [legalResearchResult]);
  const legalRelatedCases = useMemo(() => {
    const items = Array.isArray(legalResearchResult?.relatedCases) ? legalResearchResult.relatedCases : [];
    return items.filter((item) => !hasMockMarker(item));
  }, [legalResearchResult]);
  const legalAnswer = useMemo(() => {
    const raw = legalResearchResult?.answer || '';
    return raw
      .split(/\r?\n/)
      .filter((line) => !hasMockMarker(line))
      .join('\n')
      .trim() || 'Geen antwoord gevonden.';
  }, [legalResearchResult]);
  const legalAppliedDomains = useMemo(() => {
    const items = Array.isArray(legalResearchResult?.appliedLegalDomains) ? legalResearchResult.appliedLegalDomains : [];
    return items.map((id) => LEGAL_DOMAIN_LABELS[id] || id);
  }, [legalResearchResult]);
  const legalDocumentIds = useMemo(() => documents.map((doc) => doc.id), [documents]);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await qaApi.list(workspaceId);
      setQuestions(res.data || []);
    } catch (err) {
      console.error('Failed to load Q&A', err);
      setQuestions([]);
      if (err?.response?.status === 401) {
        setErrorMessage('Your session is not authorized for this request. Please sign in again.');
        setErrorMessage(err?.response?.data?.message || 'You do not have access to this workspace.');
      } else {
        setErrorMessage('Unable to load document chat right now. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const loadComments = useCallback(async (questionId) => {
    try {
      const res = await commentApi.list(questionId);
      setComments(res.data || []);
    } catch (err) {
      setComments([]);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    setSelectedQ(null);
    prevSyncedQuestionIdRef.current = null;
  }, [workspaceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await securityApi.bootstrapCsrf();
      } catch {
        // Ignore bootstrap failure here; the submit/refresh action will surface any real error.
      } finally {
        if (!cancelled) {
          setLegalBootstrapReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await workspaceApi.get(workspaceId);
        if (!cancelled) setWorkspaceRole(res.data?.userRole ?? null);
      } catch {
        if (!cancelled) setWorkspaceRole(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await documentApi.list(workspaceId);
        if (!cancelled) setDocuments(res.data || []);
      } catch {
        if (!cancelled) setDocuments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedQ?.id) {
      setAgendaItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await qaApi.listAgendaItems(workspaceId, selectedQ.id);
        if (!cancelled) setAgendaItems(res.data || []);
      } catch {
        if (!cancelled) setAgendaItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, selectedQ]);

  useEffect(() => {
    if (!selectedQ?.id) {
      setCaseForm({
        location: '',
        contactPerson: '',
        phone: '',
        email: '',
        notes: '',
        driveFrom: '',
        driveTo: '',
      });
      setTodoItems([]);
      return;
    }
    const key = `ventus_case_${workspaceId}_${selectedQ.id}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setCaseForm((prev) => ({ ...prev, ...(parsed.caseForm || {}) }));
      setTodoItems(Array.isArray(parsed.todoItems) ? parsed.todoItems : []);
    } catch {
      // ignore malformed local storage
    }
  }, [workspaceId, selectedQ?.id]);

  useEffect(() => {
    const imageDocs = documents.filter((doc) => isImageDoc(doc));
    const missing = imageDocs.filter((doc) => !imagePreviewUrls[doc.id] && !imagePreviewFailedIds[doc.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const next = {};
      const failed = {};
      const attemptsUpdate = {};
      const errorsUpdate = {};
      for (const doc of missing) {
        try {
          let res;
          try {
            // First attempt inline=true for browser-friendly content-disposition
            res = await documentApi.getFileBlob(workspaceId, doc.id, true);
          } catch {
            // Fallback: some servers/proxies are stricter on inline mode
            res = await documentApi.getFileBlob(workspaceId, doc.id, false);
          }
          const contentType = res.headers?.['content-type'] || doc.mimeType || 'image/*';
          const blob = new Blob([res.data], { type: contentType });
          const objectUrl = URL.createObjectURL(blob);
          next[doc.id] = objectUrl;
          attemptsUpdate[doc.id] = 0;
          setImagePreviewFailedIds((prev) => {
            const cleared = { ...prev };
            delete cleared[doc.id];
            return cleared;
          });
          errorsUpdate[doc.id] = '';
        } catch (err) {
          const status = err?.response?.status;
          const attempt = (imagePreviewAttemptCounts[doc.id] || 0) + 1;
          attemptsUpdate[doc.id] = attempt;
          errorsUpdate[doc.id] = status ? `HTTP ${status}` : (err?.message || 'Preview failed');
          if (status === 404 || attempt >= 3) {
            failed[doc.id] = true;
          }
          if (status !== 404) {
            console.error('Preview load failed', err);
          }
        }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setImagePreviewUrls((prev) => {
          const merged = { ...prev, ...next };
          previewUrlsRef.current = merged;
          return merged;
        });
      } else if (cancelled) {
        Object.values(next).forEach((url) => URL.revokeObjectURL(url));
      }
      if (!cancelled && Object.keys(failed).length > 0) {
        setImagePreviewFailedIds((prev) => ({ ...prev, ...failed }));
      }
      if (!cancelled && Object.keys(attemptsUpdate).length > 0) {
        setImagePreviewAttemptCounts((prev) => ({ ...prev, ...attemptsUpdate }));
      }
      if (!cancelled && Object.keys(errorsUpdate).length > 0) {
        setImagePreviewErrorById((prev) => ({ ...prev, ...errorsUpdate }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documents, imagePreviewUrls, imagePreviewFailedIds, imagePreviewAttemptCounts, workspaceId]);

  useEffect(() => () => {
    Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    if (!openImageDocId) return;
    const hasPreview = Boolean(imagePreviewUrls[openImageDocId]);
    if (!hasPreview) {
      setOpenImageDocId(null);
    }
  }, [openImageDocId, imagePreviewUrls]);

  useEffect(() => {
    if (questions.length === 0) return;

    const raw = searchParams.get('questionId');
    const wantedId = raw != null && raw !== '' ? Number(raw) : NaN;

    if (Number.isFinite(wantedId)) {
      const found = questions.find((q) => q.id === wantedId);
      if (found) {
        if (prevSyncedQuestionIdRef.current !== wantedId) {
          prevSyncedQuestionIdRef.current = wantedId;
          setSelectedQ(found);
          loadComments(wantedId);
        }
        return;
      }
      prevSyncedQuestionIdRef.current = null;
      setSearchParams({}, { replace: true });
      return;
    }

    const first = questions[0];
    if (first && prevSyncedQuestionIdRef.current !== first.id) {
      prevSyncedQuestionIdRef.current = first.id;
      setSelectedQ(first);
      setSearchParams({ questionId: String(first.id) }, { replace: true });
      loadComments(first.id);
    }
  }, [questions, searchParams, setSearchParams, loadComments]);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setAsking(true);

    try {
      const res = await qaApi.ask(workspaceId, question.trim());
      const created = res.data;
      setQuestions((prev) => [created, ...prev]);
      prevSyncedQuestionIdRef.current = created.id;
      setSelectedQ(created);
      setSearchParams({ questionId: String(created.id) }, { replace: true });
      await loadComments(created.id);
      setQuestion('');
      setErrorMessage('');
    } catch (err) {
      console.error('Q&A failed', err);
      if (err?.response?.status === 401) {
        setErrorMessage('Your session is not authorized for this request. Please sign in again.');
      } else if (err?.response?.status === 403) {
        setErrorMessage(err?.response?.data?.message || 'You do not have access to this workspace.');
      } else {
        setErrorMessage('Question failed. Please try again.');
      }
    } finally {
      setAsking(false);
    }
  };

  const handleVote = async (qId, upvote) => {
    try {
      await qaApi.vote(workspaceId, qId, upvote);
      setQuestions((prev) => {
        const updated = prev.map((q) => {
          if (q.id !== qId) return q;
          return {
            ...q,
            upvotes: upvote ? q.upvotes + 1 : q.upvotes,
            downvotes: upvote ? q.downvotes : q.downvotes + 1
          };
        });

        const updatedQuestion = updated.find((q) => q.id === qId);
        if (updatedQuestion) {
          setSelectedQ(updatedQuestion);
        }

        return updated;
      });
    } catch (err) {
      console.error('Vote failed', err);
    }
  };

  const selectQuestion = (q) => {
    setSearchParams({ questionId: String(q.id) }, { replace: true });
  };

  const mergeQuestion = (updated) => {
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)));
    setSelectedQ((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const handleClaim = async () => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.claim(workspaceId, selectedQ.id);
      mergeQuestion(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not take this thread.';
      window.alert(msg);
    }
  };

  const handleReleaseAssignment = async () => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.releaseAssignment(workspaceId, selectedQ.id);
      mergeQuestion(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not release this assignment.';
      window.alert(msg);
    }
  };

  const handleApproveOfficial = async () => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.approve(workspaceId, selectedQ.id);
      mergeQuestion(res.data);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Approve failed.');
    }
  };

  const handleTogglePrivateQuestion = async (makePrivate) => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.setPrivacy(workspaceId, selectedQ.id, makePrivate);
      mergeQuestion(res.data);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not change privacy.');
    }
  };

  const handleApprovePrivateVisibility = async () => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.approvePrivateVisibility(workspaceId, selectedQ.id);
      mergeQuestion(res.data);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not approve private visibility.');
    }
  };

  const handleExportThread = async () => {
    if (!selectedQ) return;
    try {
      const res = await qaApi.exportMarkdown(workspaceId, selectedQ.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ventus-qa-${selectedQ.id}.md`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('Export failed.');
    }
  };

  const canUploadDocuments = ['OWNER', 'EDITOR', 'ADMIN'].includes(workspaceRole || '');

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      await documentApi.upload(workspaceId, file);
      const res = await documentApi.list(workspaceId);
      setDocuments(res.data || []);
      // If a new image arrives, allow fresh preview attempts even after older failures.
      setImagePreviewFailedIds({});
      setImagePreviewAttemptCounts({});
    } catch {
      window.alert('Document upload failed.');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleSelection = (current, id) =>
    current.includes(id) ? current.filter((x) => x !== id) : [...current, id];

  const openReportModal = () => {
    const preSelectedQuestionIds = selectedQ?.id ? [selectedQ.id] : [];
    setReportQuestionIds(preSelectedQuestionIds);
    setReportDocumentIds(documents.slice(0, 3).map((d) => d.id));
    setReportTitle('Investigation report');
    setReportModalOpen(true);
  };

  const handleExportPdfReport = async () => {
    if (reportQuestionIds.length === 0 && reportDocumentIds.length === 0) {
      window.alert('Select at least one question or document.');
      return;
    }
    setReportBusy(true);
    try {
      const res = await qaApi.exportPdfReport(workspaceId, {
        title: reportTitle.trim() || 'Investigation report',
        questionIds: reportQuestionIds,
        documentIds: reportDocumentIds,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(reportTitle.trim() || 'investigation-report').replace(/[^a-zA-Z0-9-_]+/g, '-')}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReportModalOpen(false);
    } catch {
      window.alert('Could not generate report PDF.');
    } finally {
      setReportBusy(false);
    }
  };

  const toMultiline = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

  const handleGenerateAgendaProposal = async () => {
    if (!selectedQ) return;
    setAgendaBusy(true);
    setAgendaError('');
    try {
      const res = await qaApi.agendaProposal(workspaceId, selectedQ.id);
      const p = res.data || {};
      setAgendaDraft({
        title: p.title || `Follow-up: ${selectedQ.questionText?.slice(0, 70) || 'Q&A topic'}`,
        objective: p.objective || '',
        agendaPoints: toMultiline(p.agendaPoints),
        decisionsNeeded: toMultiline(p.decisionsNeeded),
        dueAt: '',
      });
      setAgendaModalOpen(true);
    } catch (err) {
      setAgendaError(err?.response?.data?.message || 'Kon agenda-voorstel niet genereren.');
    } finally {
      setAgendaBusy(false);
    }
  };

  const handleSaveAgendaItem = async () => {
    if (!selectedQ) return;
    if (!agendaDraft.title.trim()) {
      setAgendaError('Titel is verplicht.');
      return;
    }
    setAgendaBusy(true);
    setAgendaError('');
    try {
      const payload = {
        title: agendaDraft.title.trim(),
        objective: agendaDraft.objective.trim(),
        agendaPoints: agendaDraft.agendaPoints
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        decisionsNeeded: agendaDraft.decisionsNeeded
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        dueAt: agendaDraft.dueAt ? new Date(agendaDraft.dueAt).toISOString().slice(0, 19) : null,
      };
      const res = await qaApi.createAgendaItem(workspaceId, selectedQ.id, payload);
      setAgendaItems((prev) => [res.data, ...prev]);
      setAgendaModalOpen(false);
    } catch (err) {
      setAgendaError(err?.response?.data?.message || 'Opslaan agenda-item mislukt.');
    } finally {
      setAgendaBusy(false);
    }
  };

  const currentUserId = user?.userId != null ? Number(user.userId) : null;
  const canTakeOverAssignment = ['OWNER', 'EDITOR', 'ADMIN'].includes(workspaceRole || '');
  const assigneeId = selectedQ?.assignedTo != null ? Number(selectedQ.assignedTo) : null;
  const isAssignee = currentUserId != null && assigneeId === currentUserId;
  const assignedToOther =
    assigneeId != null && currentUserId != null && assigneeId !== currentUserId;

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedQ) return;
    try {
      const res = await commentApi.add(selectedQ.id, newComment.trim());
      setComments((prev) => [...prev, res.data]);
      setNewComment('');
    } catch (err) {
      console.error('Comment failed', err);
    }
  };

  const citations = useMemo(() => {
    if (!selectedQ?.citations) return [];
    try {
      return JSON.parse(selectedQ.citations);
    } catch {
      return [];
    }
  }, [selectedQ]);

  const isImageDoc = (doc) => (doc?.mimeType || '').toLowerCase().startsWith('image/');
  const retryImagePreview = (docId) => {
    setImagePreviewFailedIds((prev) => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    setImagePreviewAttemptCounts((prev) => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    setImagePreviewErrorById((prev) => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
  };
  const openImageWithoutPreview = async (doc) => {
    try {
      const res = await documentApi.getFileBlob(workspaceId, doc.id, false);
      const ct = res.headers?.['content-type'] || doc.mimeType || 'image/*';
      const blob = new Blob([res.data], { type: ct });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not open image file.');
    }
  };
  const openImageDoc = documents.find((doc) => doc.id === openImageDocId) || null;

  const updateCaseForm = (field, value) => {
    setCaseForm((prev) => {
      const next = { ...prev, [field]: value };
      if (selectedQ?.id) {
        const key = `ventus_case_${workspaceId}_${selectedQ.id}`;
        const payload = { caseForm: next, todoItems };
        localStorage.setItem(key, JSON.stringify(payload));
      }
      return next;
    });
  };

  const persistTodo = (nextItems) => {
    setTodoItems(nextItems);
    if (selectedQ?.id) {
      const key = `ventus_case_${workspaceId}_${selectedQ.id}`;
      const payload = { caseForm, todoItems: nextItems };
      localStorage.setItem(key, JSON.stringify(payload));
    }
  };

  const addTodo = () => {
    const text = newTodo.trim();
    if (!text) return;
    persistTodo([...todoItems, { id: Date.now(), text, done: false }]);
    setNewTodo('');
  };

  const toggleTodo = (id) => {
    persistTodo(todoItems.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  };

  const removeTodo = (id) => {
    persistTodo(todoItems.filter((t) => t.id !== id));
  };

  const suggestTodos = () => {
    if (!selectedQ || todoSuggesting) return;
    (async () => {
      setTodoSuggesting(true);
      try {
        const res = await qaApi.todoSuggestions(workspaceId, selectedQ.id);
        const suggestions = normalizeTodoSuggestions(res?.data?.items ?? res?.data);
        const cleanedCurrent = todoItems.filter((t) => {
          const text = String(t?.text || '').trim().toLowerCase();
          if (!text) return false;
          for (const prefix of LEGACY_TODO_TEMPLATES) {
            if (text.startsWith(prefix)) return false;
          }
          return true;
        });
        const existing = new Set(cleanedCurrent.map((t) => t.text.toLowerCase()));
        const next = [...cleanedCurrent];
        suggestions.forEach((text) => {
          const clean = cleanTodoSuggestionText(text);
          if (clean && !existing.has(clean.toLowerCase())) {
            next.push({ id: Date.now() + next.length, text: clean, done: false });
          }
        });
        persistTodo(next);
      } catch (err) {
        window.alert(err?.response?.data?.message || 'Could not generate AI to-do suggestions.');
      } finally {
        setTodoSuggesting(false);
      }
    })();
  };

  const driveUrl = useMemo(() => {
    const from = (caseForm.driveFrom || '').trim();
    const to = (caseForm.driveTo || caseForm.location || '').trim();
    if (!from || !to) return null;
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(from)}&destination=${encodeURIComponent(to)}&travelmode=driving`;
  }, [caseForm.driveFrom, caseForm.driveTo, caseForm.location]);

  const runLegalResearch = useCallback(async (options = {}) => {
    if (!legalBootstrapReady) return false;
    if (legalDocumentIds.length === 0) {
      setLegalResearchResult(null);
      setLegalResearchError('Upload a document to generate automatic legal matches.');
      return false;
    }

    const lawQuery = (options.lawQuery ?? legalSearchQuery).trim();
    const caseQuery = (options.caseQuery ?? legalCaseSearchQuery).trim();

    setLegalResearching(true);
    setLegalResearchError('');
    try {
      const { data } = await legalApi.research({
        workspaceId: Number(workspaceId),
        documentIds: legalDocumentIds,
        question: selectedQ?.questionText?.trim() || undefined,
        lawQuery: lawQuery || undefined,
        caseQuery: caseQuery || undefined,
        legalDomains: legalDomains.length > 0 ? legalDomains : undefined,
      });
      setLegalResearchResult(data || null);
      if ((data?.relatedCases?.length || 0) > 0 || (data?.citations || []).some((c) => String(c?.sourceType || '').toUpperCase() === 'CASE')) {
        setLegalCasesOpen(true);
      }
      return true;
    } catch (err) {
      setLegalResearchResult(null);
      setLegalResearchError(err?.response?.data?.message || err?.message || 'Legal research request failed. Please try again.');
      return false;
    } finally {
      setLegalResearching(false);
    }
  }, [legalBootstrapReady, legalCaseSearchQuery, legalDocumentIds, legalDomains, legalSearchQuery, selectedQ?.questionText, workspaceId]);

  useEffect(() => {
    setLegalDomains(readStoredLegalDomains(workspaceId));
  }, [workspaceId]);

  const toggleLegalDomain = (domainId) => {
    setLegalDomains((prev) => {
      const next = prev.includes(domainId)
        ? prev.filter((id) => id !== domainId)
        : [...prev, domainId];
      try {
        window.localStorage.setItem(`ventus:legal-domains:${workspaceId}`, JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  useEffect(() => {
    if (!legalBootstrapReady) return;
    void runLegalResearch({ lawQuery: '', caseQuery: '' });
  }, [legalBootstrapReady, legalDocumentIds, selectedQ?.id, runLegalResearch]);

  const handleRefreshLegalResearch = async () => {
    await runLegalResearch({});
  };

  const handleLegalSearch = async (e) => {
    e.preventDefault();
    await runLegalResearch({});
  };

  if (loading) return <div className="v-loading">Loading document chat...</div>;

  return (
    <div className={`v-chat-page ${focusMode ? 'v-chat-page--focus' : ''}`}>
      <div className="v-chat-topbar">
        <div className="v-chat-breadcrumb">
          <button onClick={() => navigate('/workspaces')}>Workspaces</button>
          <span>/</span>
          <button type="button" onClick={() => navigate(`/workspaces/${workspaceId}`)}>Workspace</button>
          <span>/</span>
          <button type="button" onClick={() => navigate(`/workspaces/${workspaceId}/search`)}>Search</button>
          <span>/</span>
          <span>Document Chat</span>
        </div>
        <div className="v-chat-headline">
          <h1>Document Intelligence Chat</h1>
          <p>Ask, review evidence, and discuss answers in one focused workspace.</p>
          {showHeaderStats && (
            <div className="v-chat-headline__meta">
              <span className="v-chat-pill">{questions.length} threads</span>
              <span className="v-chat-pill">{documents.length} documents</span>
              {selectedQ && <span className="v-chat-pill v-chat-pill--active">Active: {selectedQ.status}</span>}
            </div>
          )}
          <div className="v-chat-headline__actions">
            <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={() => setShowHeaderStats((v) => !v)}>
              {showHeaderStats ? 'Hide stats' : 'Show stats'}
            </button>
            <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={() => setFocusMode((v) => !v)}>
              {focusMode ? 'Exit focus mode' : 'Focus mode'}
            </button>
          </div>
        </div>
      </div>

      <section className="v-legal-inline">
        <div className="v-legal-inline__header">
          <div>
            <div className="v-chat-panel-title">Legal context</div>
            <h2>Document-linked legal context</h2>
            <p>The panel scans uploaded documents automatically and refreshes the extracted cases and related items when you click rescan.</p>
          </div>
          <div className="v-legal-inline__actions">
            <span className="v-chat-pill v-chat-pill--active">Document-driven</span>
            <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleRefreshLegalResearch} disabled={legalResearching || !legalBootstrapReady}>
              {legalResearching ? 'Refreshing...' : 'Refresh legal matches'}
            </button>
          </div>
        </div>

        <div className="v-legal-inline__visibility">
          <label className="v-legal-toggle-check">
            <input
              type="checkbox"
              checked={legalPanelOpen}
              onChange={(e) => setLegalPanelOpen(e.target.checked)}
            />
            <span>Show legal panel</span>
          </label>

          <label className="v-legal-toggle-check">
            <input
              type="checkbox"
              checked={legalDetailsOpen}
              onChange={(e) => setLegalDetailsOpen(e.target.checked)}
              disabled={!legalPanelOpen || !legalResearchResult}
            />
            <span>Show legal details</span>
          </label>

          <label className="v-legal-toggle-check">
            <input
              type="checkbox"
              checked={legalAssistantOpen}
              onChange={(e) => setLegalAssistantOpen(e.target.checked)}
              disabled={!legalPanelOpen || !legalResearchResult}
            />
            <span>Show assistant summary</span>
          </label>
        </div>

        {legalPanelOpen && <div className="v-legal-inline__note">
          This section updates automatically when documents are uploaded. Use refresh to rescan the current files.
        </div>}

        {legalPanelOpen && (
          <div className="v-legal-inline__domains">
            <div className="v-legal-inline__domains-head">
              <span className="v-legal-inline__domains-label">Rechtsgebied</span>
              <span className="v-legal-inline__domains-hint">
                {legalDomains.length === 0
                  ? 'Automatisch op basis van documenten en vraag'
                  : 'Handmatig geselecteerd — klik opnieuw op Refresh legal matches'}
              </span>
            </div>
            <div className="v-legal-inline__domain-options">
              {LEGAL_DOMAIN_OPTIONS.map((option) => {
                const checked = legalDomains.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className={`v-legal-domain-chip ${checked ? 'is-active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleLegalDomain(option.id)}
                      disabled={legalResearching || !legalBootstrapReady}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {legalPanelOpen && (
          <form className="v-legal-inline__form" onSubmit={handleLegalSearch}>
            <label className="v-legal-inline__field">
              <span className="v-legal-inline__field-label">Wetten zoeken</span>
              <input
                className="v-legal-inline__input v-legal-inline__input--compact v-chat-input"
                type="text"
                value={legalSearchQuery}
                onChange={(e) => setLegalSearchQuery(e.target.value)}
                placeholder="bv. vakantiedagen minimumloon arbeidsovereenkomst..."
                disabled={legalResearching || !legalBootstrapReady}
              />
            </label>
            <label className="v-legal-inline__field">
              <span className="v-legal-inline__field-label">Vergelijkbare uitspraken</span>
              <input
                className="v-legal-inline__input v-legal-inline__input--compact v-chat-input"
                type="text"
                value={legalCaseSearchQuery}
                onChange={(e) => setLegalCaseSearchQuery(e.target.value)}
                placeholder="bv. vakantiedagen ontslag werknemer of voorrang haaientanden..."
                disabled={legalResearching || !legalBootstrapReady}
              />
            </label>
            <div className="v-legal-inline__form-actions">
              <button type="submit" className="v-btn v-btn--ghost v-btn--sm" disabled={legalResearching || !legalBootstrapReady}>
                {legalResearching ? 'Zoeken...' : 'Zoek wetten en uitspraken'}
              </button>
            </div>
          </form>
        )}

        {legalPanelOpen && legalResearchError && <div className="v-chat-empty v-chat-empty--tight">{legalResearchError}</div>}

        {legalPanelOpen && legalResearchResult?.catalogLawCount === 0 && (
          <div className="v-chat-empty v-chat-empty--tight">
            Juridische wet-catalogus is nog leeg op deze server. Admin: voer Legal sync uit via Admin, of POST /api/admin/legal/sync.
          </div>
        )}

        {legalPanelOpen && (legalResearchResult ? (
          <div className={`v-legal-inline__result ${legalDetailsOpen ? '' : 'is-condensed'}`}>
            {legalAssistantOpen && (
              <div className="v-message v-message--assistant">
                <div className="v-message__label">Private assistant</div>
                <div className="v-message__body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {legalAnswer}
                  </ReactMarkdown>
                </div>
                <div className="v-message__footer">
                  <span>
                    {legalMatchedChunks.length > 0 ? `${legalMatchedChunks.length} documentfragment${legalMatchedChunks.length === 1 ? '' : 'en'}` : 'Geen documentfragmenten'}
                    {legalArticleCitations.length > 0 ? ` · ${legalArticleCitations.length} wet${legalArticleCitations.length === 1 ? '' : 'ten'}` : ''}
                    {legalCaseCitations.length > 0 ? ` · ${legalCaseCitations.length} uitspra${legalCaseCitations.length === 1 ? 'ak' : 'ken'}` : ''}
                    {legalAppliedDomains.length > 0 ? ` · ${legalAppliedDomains.join(', ')}` : ' · automatisch rechtsgebied'}
                  </span>
                </div>
              </div>
            )}

            {!legalDetailsOpen && (
              <div className="v-legal-inline__summary">
                <strong>{legalMatchedChunks.length}</strong> document fragments
                {legalArticleCitations.length > 0 && (
                  <> · <strong>{legalArticleCitations.length}</strong> linked law article{legalArticleCitations.length === 1 ? '' : 's'}</>
                )}
                {legalArticleCitations.length === 0 && legalRelatedArticles.length === 0 && (
                  <> · no statutory articles matched yet — try <strong>Refresh legal matches</strong> after upload</>
                )}
                . Use <strong>Show legal details</strong> for full citations.
              </div>
            )}

            {legalDetailsOpen && <div className="v-legal-grid">
              <div className="v-citation-block">
                <div className="v-chat-panel-title">References</div>
                {legalSourceCount > 0 ? (
                  <div className="v-citations">
                    {legalCitations.map((cite, index) => (
                      <div key={`${cite.sourceId || index}`} className="v-citation">
                        <div className="v-citation__header">
                          <span className="v-citation__number">{index + 1}</span>
                          <span className="v-citation__doc">{cite.title || cite.sourceId}</span>
                          {cite.versionDate && <span className="v-citation__page">{cite.versionDate}</span>}
                        </div>
                        <p className="v-citation__snippet">
                          {cite.reference ? `${cite.reference} · ` : ''}{cite.quotedText || 'Officiële bronverwijzing.'}
                        </p>
                        {cite.url && (
                          <a href={cite.url} target="_blank" rel="noreferrer" className="v-case-board__route">
                            Open bron
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="v-chat-empty v-chat-empty--tight">Nog geen bronnen beschikbaar.</div>
                )}
              </div>

              <div>
                <div className="v-legal-section-toggle-row">
                  <button
                    type="button"
                    className="v-chat-panel-toggle"
                    onClick={() => setLegalArticlesOpen((v) => !v)}
                  >
                    {legalArticlesOpen ? 'Hide' : 'Show'} articles
                  </button>
                </div>
                {legalArticlesOpen && (
                  <>
                    <div className="v-chat-panel-title">Articles</div>
                    {legalRelatedArticles.length > 0 ? (
                      <div className="v-legal-list">
                        {legalRelatedArticles.map((item) => (
                          <div key={item} className="v-legal-list__item">{item}</div>
                        ))}
                      </div>
                    ) : (
                      <div className="v-chat-empty v-chat-empty--tight">No related articles found.</div>
                    )}

                    {legalArticleCitations.length > 0 && (
                      <div className="v-citations v-legal-inline-citations">
                        {legalArticleCitations.map((cite, index) => (
                          <div key={`${cite.sourceId || index}`} className="v-citation">
                            <div className="v-citation__header">
                              <span className="v-citation__number">{index + 1}</span>
                              <span className="v-citation__doc">{cite.title || cite.sourceId}</span>
                              {cite.versionDate && <span className="v-citation__page">{cite.versionDate}</span>}
                            </div>
                            <p className="v-citation__snippet">
                              {cite.reference ? `${cite.reference} · ` : ''}{cite.quotedText || 'Officiële bronverwijzing.'}
                            </p>
                            {cite.url && (
                              <a href={cite.url} target="_blank" rel="noreferrer" className="v-case-board__route">
                                Open bron
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <div className="v-legal-section-toggle-row v-legal-section-toggle-row--spaced">
                  <button
                    type="button"
                    className="v-chat-panel-toggle"
                    onClick={() => setLegalCasesOpen((v) => !v)}
                  >
                    {legalCasesOpen ? 'Verberg' : 'Toon'} uitspraken
                  </button>
                </div>
                {legalCasesOpen && (
                  <>
                    <div className="v-chat-panel-title">Vergelijkbare uitspraken (Rechtspraak.nl)</div>
                    {legalCaseCitations.length === 0 && legalRelatedCases.length === 0 ? (
                      <div className="v-chat-empty v-chat-empty--tight">
                        Geen uitspraken gevonden. Vul hierboven een zoekterm in, bv. &quot;vakantiedagen werknemer&quot; of &quot;voorrang haaientanden&quot;.
                      </div>
                    ) : (
                      <>
                        {legalRelatedCases.length > 0 && (
                          <div className="v-legal-list">
                            {legalRelatedCases.map((item) => (
                              <div key={item} className="v-legal-list__item">{item}</div>
                            ))}
                          </div>
                        )}

                        {legalCaseCitations.length > 0 && (
                          <div className="v-citations v-legal-inline-citations">
                            {legalCaseCitations.map((cite, index) => (
                              <div key={`${cite.sourceId || index}`} className="v-citation">
                                <div className="v-citation__header">
                                  <span className="v-citation__number">{index + 1}</span>
                                  <span className="v-citation__doc">{cite.title || cite.sourceId}</span>
                                  {cite.versionDate && <span className="v-citation__page">{cite.versionDate}</span>}
                                </div>
                                <p className="v-citation__snippet">
                                  {cite.reference ? `${cite.reference} · ` : ''}{cite.quotedText || 'Rechtspraakverwijzing.'}
                                </p>
                                {cite.url && (
                                  <a href={cite.url} target="_blank" rel="noreferrer" className="v-case-board__route">
                                    Open uitspraak
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="v-chat-panel-title">Exact matched chunks</div>
                {legalMatchedChunks.length > 0 ? (
                  <div className="v-legal-match-stack">
                    {legalMatchedChunks.map((cite, index) => (
                      <article key={`${cite.sourceId || index}`} className="v-legal-match-card">
                        <div className="v-legal-match-card__top">
                          <span className="v-legal-match-card__badge">Chunk {index + 1}</span>
                          <span className="v-legal-match-card__doc">{cite.title || cite.sourceId}</span>
                        </div>
                        <p className="v-legal-match-card__snippet">
                          {cite.quotedText || 'Documentfragment'}
                        </p>
                        <div className="v-legal-match-card__meta">
                          {cite.reference && <span>{cite.reference}</span>}
                          {cite.versionDate && <span>{cite.versionDate}</span>}
                        </div>
                        {cite.url && (
                          <a href={cite.url} target="_blank" rel="noreferrer" className="v-case-board__route">
                            Open bron
                          </a>
                        )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="v-chat-empty v-chat-empty--tight">No matched chunks yet.</div>
                )}
              </div>
            </div>}
          </div>
        ) : (
          <div className="v-chat-empty">Ask a confidential question to pull workspace-specific sources and context.</div>
        ))}
      </section>

      <form className="v-chat-composer" onSubmit={handleAsk}>
        <textarea
          className="v-chat-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a question about contracts, invoices, policies, or any uploaded document..."
          rows={2}
          disabled={asking}
        />
        <div className="v-chat-composer__actions">
          <button type="submit" className="v-btn v-btn--primary" disabled={asking || !question.trim()}>
            {asking ? 'Thinking...' : 'Ask Ventus'}
          </button>
          <button type="button" className="v-btn v-btn--ghost" onClick={openReportModal}>
            Generate report PDF
          </button>
        </div>
      </form>
      {errorMessage && <div className="v-chat-empty v-chat-empty--tight">{errorMessage}</div>}

      <div className="v-chat-layout">
        {!focusMode && (
          <aside className={`v-chat-threads ${threadsOpen ? 'is-open' : 'is-collapsed'}`}>
            <div className="v-chat-panel-head">
              <div className="v-chat-panel-title">Threads</div>
              <button type="button" className="v-chat-panel-toggle" onClick={() => setThreadsOpen((v) => !v)}>
                {threadsOpen ? 'Collapse' : `Show (${questions.length})`}
              </button>
            </div>
            {threadsOpen && (
              <>
                {questions.length === 0 ? (
                  <div className="v-chat-empty">No threads yet. Ask your first question.</div>
                ) : (
                  <div className="v-thread-list">
                    {questions.map((q) => (
                      <button
                        key={q.id}
                        type="button"
                        className={`v-thread-item ${selectedQ?.id === q.id ? 'is-active' : ''}`}
                        onClick={() => selectQuestion(q)}
                      >
                        <div className="v-thread-item__top">
                          <span className={statusBadgeClass(q.status)}>{q.status}</span>
                          {q.approved && <span className="v-badge v-badge--success">Official</span>}
                          {q.privateQuestion && <span className="v-badge v-badge--warning">Private</span>}
                          <span className="v-thread-item__time">{new Date(q.createdAt).toLocaleDateString()}</span>
                        </div>
                        <p className="v-thread-item__text">{q.questionText}</p>
                        <div className="v-thread-item__people">
                          Asked by <span className="v-thread-item__name">{q.askedByName || 'Unknown'}</span>
                          {q.assignedToName ? (
                            <>
                              {' · '}
                              <span className="v-thread-item__assign">{q.assignedToName}</span> follow-up
                            </>
                          ) : null}
                        </div>
                        <div className="v-thread-item__meta">
                          <span>Up {q.upvotes}</span>
                          <span>Down {q.downvotes}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </aside>
        )}

        <section className="v-chat-conversation">
          {!selectedQ ? (
            <div className="v-chat-empty">Select a thread to view the conversation.</div>
          ) : (
            <>
              <div className="v-message v-message--user">
                <div className="v-message__label">Question</div>
                <div className="v-message__people">
                  <span>
                    Asked by <strong>{selectedQ.askedByName || 'Unknown'}</strong>
                  </span>
                  <span className="v-message__people-sep">·</span>
                  <span>
                    Follow-up:{' '}
                    {selectedQ.assignedToName ? (
                      <strong>{selectedQ.assignedToName}</strong>
                    ) : (
                      <em className="v-message__people-muted">nobody assigned yet</em>
                    )}
                  </span>
                </div>
                <div className="v-message__assignment-actions">
                  {assigneeId == null && (
                    <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleClaim}>
                      I&apos;m handling this
                    </button>
                  )}
                  {isAssignee && (
                    <button
                      type="button"
                      className="v-btn v-btn--ghost v-btn--sm"
                      onClick={handleReleaseAssignment}
                    >
                      Done — release
                    </button>
                  )}
                  {assignedToOther && canTakeOverAssignment && (
                    <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleClaim}>
                      Assign follow-up to me
                    </button>
                  )}
                </div>
                <p>{selectedQ.questionText}</p>
              </div>

              <div className="v-message v-message--assistant">
                <div className="v-message__label">Ventus Answer</div>
                <div className="v-message__body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedQ.answerText || 'No answer yet...'}
                  </ReactMarkdown>
                </div>
                {selectedQ.status === 'ANSWERED' && (
                  <div className="v-message__governance">
                    {selectedQ.confidenceScore != null && selectedQ.confidenceScore <= 42 && (
                      <p className="v-chat-low-evidence">
                        Weak source match — verify this answer against the cited documents.
                      </p>
                    )}
                    <div className="v-message__governance-actions">
                      <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleExportThread}>
                        Export thread (Markdown)
                      </button>
                      <button
                        type="button"
                        className="v-btn v-btn--ghost v-btn--sm"
                        onClick={handleGenerateAgendaProposal}
                        disabled={agendaBusy}
                      >
                        {agendaBusy ? 'Voorstel maken...' : 'Maak agenda-item'}
                      </button>
                      {canTakeOverAssignment && !selectedQ.approved && (
                        <button type="button" className="v-btn v-btn--primary v-btn--sm" onClick={handleApproveOfficial}>
                          Mark official answer
                        </button>
                      )}
                      {selectedQ.approved && (
                        <span className="v-badge v-badge--success">Official answer ({selectedQ.approvedByName || 'editor'})</span>
                      )}
                      <button
                        type="button"
                        className="v-btn v-btn--ghost v-btn--sm"
                        onClick={() => handleTogglePrivateQuestion(!selectedQ.privateQuestion)}
                      >
                        {selectedQ.privateQuestion ? 'Mark public' : 'Mark private'}
                      </button>
                      {selectedQ.privateQuestion && !selectedQ.privateVisibilityApproved && workspaceRole === 'OWNER' && (
                        <button
                          type="button"
                          className="v-btn v-btn--ghost v-btn--sm"
                          onClick={handleApprovePrivateVisibility}
                        >
                          Approve admin visibility
                        </button>
                      )}
                      {selectedQ.privateQuestion && (
                        <span className="v-badge v-badge--warning">
                          {selectedQ.privateVisibilityApproved ? 'Private approved for admin' : 'Private masked in admin'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="v-message__footer">
                  {selectedQ.confidenceScore > 0 && <span>Confidence: {selectedQ.confidenceScore}%</span>}
                  {selectedQ.responseTimeMs && <span>Latency: {(selectedQ.responseTimeMs / 1000).toFixed(1)}s</span>}
                  <div className="v-message__votes">
                    <button type="button" onClick={() => handleVote(selectedQ.id, true)}>Helpful ({selectedQ.upvotes})</button>
                    <button type="button" onClick={() => handleVote(selectedQ.id, false)}>Not Helpful ({selectedQ.downvotes})</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        {!focusMode && (
          <aside className={`v-chat-context ${contextOpen ? 'is-open' : 'is-collapsed'}`}>
            <div className="v-chat-panel-head">
              <div className="v-chat-panel-title">Workspace context</div>
              <button type="button" className="v-chat-panel-toggle" onClick={() => setContextOpen((v) => !v)}>
                {contextOpen ? 'Collapse' : 'Show'}
              </button>
            </div>
            {contextOpen && (
              <>
          <div className="v-chat-panel-title">Documents in this workspace</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleUploadDocument}
          />
          <div className="v-chat-docs-actions">
            <button
              type="button"
              className="v-btn v-btn--ghost v-btn--sm"
              onClick={() => navigate(`/workspaces/${workspaceId}/documents`)}
            >
              Open document library
            </button>
            {canUploadDocuments && (
              <button
                type="button"
                className="v-btn v-btn--primary v-btn--sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingDoc}
              >
                {uploadingDoc ? 'Uploading...' : 'Add document here'}
              </button>
            )}
          </div>
          {documents.length === 0 ? (
            <div className="v-chat-empty v-chat-empty--tight">No documents yet in this workspace.</div>
          ) : (
            <div className="v-chat-doc-list">
              {documents.slice(0, 8).map((doc) => (
                <div key={doc.id} className="v-chat-doc-item">
                  {isImageDoc(doc) ? (
                    imagePreviewUrls[doc.id] ? (
                      <button
                        type="button"
                        className="v-chat-doc-thumb-btn"
                        onClick={() => setOpenImageDocId(doc.id)}
                        title={`Open preview: ${doc.originalName || 'image'}`}
                      >
                        <img src={imagePreviewUrls[doc.id]} alt={doc.originalName || 'Preview'} className="v-chat-doc-thumb" />
                      </button>
                    ) : (
                      <div className="v-chat-doc-thumb-placeholder">
                        {imagePreviewFailedIds[doc.id] ? (
                          <>
                            <div>Preview unavailable</div>
                            <div className="v-chat-doc-thumb-error">{imagePreviewErrorById[doc.id] || ''}</div>
                            <button
                              type="button"
                              className="v-btn v-btn--ghost v-btn--sm"
                              onClick={() => retryImagePreview(doc.id)}
                            >
                              Retry preview
                            </button>
                            <button
                              type="button"
                              className="v-btn v-btn--ghost v-btn--sm"
                              onClick={() => openImageWithoutPreview(doc)}
                            >
                              Open image
                            </button>
                          </>
                        ) : 'Loading preview...'}
                      </div>
                    )
                  ) : null}
                  <strong>{doc.originalName}</strong>
                  <span>{doc.chunkCount || 0} chunks</span>
                </div>
              ))}
              {documents.length > 8 && (
                <div className="v-chat-empty v-chat-empty--tight">+{documents.length - 8} more documents</div>
              )}
            </div>
          )}

          <div className="v-chat-panel-title">Evidence</div>
          {citations.length === 0 ? (
            <div className="v-chat-empty v-chat-empty--tight">No sources for this answer yet.</div>
          ) : (
            <div className="v-citations">
              {citations.map((cite, i) => (
                <div key={i} className="v-citation">
                  <div className="v-citation__header">
                    <span className="v-citation__number">{i + 1}</span>
                    <span className="v-citation__doc">{cite.documentName}</span>
                    {cite.page && <span className="v-citation__page">p.{cite.page}</span>}
                  </div>
                  <p className="v-citation__snippet">{cite.snippet}</p>
                </div>
              ))}
            </div>
          )}

          <div className="v-chat-panel-title v-chat-panel-title--spaced">Discussion</div>
          <div className="v-comments">
            {comments.map((c) => (
              <div key={c.id} className="v-comment">
                <p>{c.content}</p>
                <span className="v-comment__meta">{new Date(c.createdAt).toLocaleString()}</span>
              </div>
            ))}
            {selectedQ && (
              <div className="v-comment-form">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add note or follow-up..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleAddComment}>
                  Send
                </button>
              </div>
            )}
          </div>

          <div className="v-chat-panel-title v-chat-panel-title--spaced">Agenda items</div>
          {agendaItems.length === 0 ? (
            <div className="v-chat-empty v-chat-empty--tight">Nog geen agenda-items voor deze thread.</div>
          ) : (
            <div className="v-citations">
              {agendaItems.map((item) => (
                <div key={item.id} className="v-citation">
                  <div className="v-citation__header">
                    <span className="v-citation__number">A</span>
                    <span className="v-citation__doc">{item.title}</span>
                  </div>
                  {item.objective ? <p className="v-citation__snippet">{item.objective}</p> : null}
                </div>
              ))}
            </div>
          )}

          <div className="v-chat-panel-title v-chat-panel-title--spaced">Case board</div>
          {selectedQ ? (
            <div className="v-case-board">
              <label>
                Location
                <input value={caseForm.location} onChange={(e) => updateCaseForm('location', e.target.value)} />
              </label>
              <label>
                Contact person
                <input value={caseForm.contactPerson} onChange={(e) => updateCaseForm('contactPerson', e.target.value)} />
              </label>
              <label>
                Phone number
                <input value={caseForm.phone} onChange={(e) => updateCaseForm('phone', e.target.value)} />
              </label>
              <label>
                E-mail
                <input value={caseForm.email} onChange={(e) => updateCaseForm('email', e.target.value)} />
              </label>
              <label>
                Drive from
                <input value={caseForm.driveFrom} onChange={(e) => updateCaseForm('driveFrom', e.target.value)} placeholder="e.g. Office address" />
              </label>
              <label>
                Drive to
                <input value={caseForm.driveTo} onChange={(e) => updateCaseForm('driveTo', e.target.value)} placeholder="defaults to location if empty" />
              </label>
              {driveUrl ? (
                <a href={driveUrl} target="_blank" rel="noreferrer" className="v-case-board__route">
                  Open route in Google Maps (shows drive time)
                </a>
              ) : (
                <span className="v-case-board__hint">Fill "Drive from" and "Drive to" to see route + drive time.</span>
              )}
              <label>
                Notes
                <textarea rows={3} value={caseForm.notes} onChange={(e) => updateCaseForm('notes', e.target.value)} />
              </label>
              <div className="v-case-todo">
                <div className="v-case-todo__head">
                  <strong>To-do list</strong>
                  <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={suggestTodos} disabled={todoSuggesting}>
                    {todoSuggesting ? 'Generating...' : 'AI suggestions'}
                  </button>
                </div>
                <div className="v-case-todo__add">
                  <input
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    placeholder="Add to-do item"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTodo(); } }}
                  />
                  <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={addTodo}>Add</button>
                </div>
                <div className="v-case-todo__items">
                  {todoItems.map((item) => (
                    <label key={item.id} className="v-case-todo__item">
                      <input type="checkbox" checked={!!item.done} onChange={() => toggleTodo(item.id)} />
                      <span className={item.done ? 'is-done' : ''}>{item.text}</span>
                      <button type="button" onClick={() => removeTodo(item.id)} title="Remove item">×</button>
                    </label>
                  ))}
                  {todoItems.length === 0 && <span className="v-case-board__hint">No to-do items yet.</span>}
                </div>
              </div>
            </div>
          ) : (
            <div className="v-chat-empty v-chat-empty--tight">Select a thread to build your case board.</div>
          )}
              </>
            )}
          </aside>
        )}
      </div>

      {agendaModalOpen && (
        <div className="v-modal-backdrop" role="presentation" onClick={() => !agendaBusy && setAgendaModalOpen(false)}>
          <div className="v-modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Agenda-item vanuit Q&A</h3>
            <p>Controleer het AI-voorstel, pas aan en sla op.</p>
            <label>
              Titel
              <input
                type="text"
                value={agendaDraft.title}
                onChange={(e) => setAgendaDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label>
              Doel
              <textarea
                rows={3}
                value={agendaDraft.objective}
                onChange={(e) => setAgendaDraft((d) => ({ ...d, objective: e.target.value }))}
              />
            </label>
            <label>
              Agendapunten (1 per regel)
              <textarea
                rows={4}
                value={agendaDraft.agendaPoints}
                onChange={(e) => setAgendaDraft((d) => ({ ...d, agendaPoints: e.target.value }))}
              />
            </label>
            <label>
              Benodigde beslissingen (1 per regel)
              <textarea
                rows={3}
                value={agendaDraft.decisionsNeeded}
                onChange={(e) => setAgendaDraft((d) => ({ ...d, decisionsNeeded: e.target.value }))}
              />
            </label>
            <label>
              Gewenste datum/tijd (optioneel)
              <input
                type="datetime-local"
                value={agendaDraft.dueAt}
                onChange={(e) => setAgendaDraft((d) => ({ ...d, dueAt: e.target.value }))}
              />
            </label>
            {agendaError ? <div className="v-chat-empty v-chat-empty--tight">{agendaError}</div> : null}
            <div className="v-modal-card__actions">
              <button type="button" className="v-btn v-btn--ghost" onClick={() => setAgendaModalOpen(false)} disabled={agendaBusy}>
                Annuleren
              </button>
              <button type="button" className="v-btn v-btn--primary" onClick={handleSaveAgendaItem} disabled={agendaBusy}>
                {agendaBusy ? 'Opslaan...' : 'Opslaan als agenda-item'}
              </button>
            </div>
          </div>
        </div>
      )}
      {reportModalOpen && (
        <div className="v-modal-backdrop" role="presentation" onClick={() => !reportBusy && setReportModalOpen(false)}>
          <div className="v-modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Generate investigation report (PDF)</h3>
            <p>Select questions and supporting documents/images to include in one downloadable report.</p>
            <label>
              Report title
              <input type="text" value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} />
            </label>
            <div className="v-report-select-grid">
              <div>
                <h4>Questions</h4>
                <div className="v-report-select-list">
                  {questions.map((q) => (
                    <label key={q.id} className="v-report-select-item">
                      <input
                        type="checkbox"
                        checked={reportQuestionIds.includes(q.id)}
                        onChange={() => setReportQuestionIds((prev) => toggleSelection(prev, q.id))}
                      />
                      <span>{q.questionText}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h4>Documents and images</h4>
                <div className="v-report-select-list">
                  {documents.map((d) => (
                    <label key={d.id} className="v-report-select-item">
                      <input
                        type="checkbox"
                        checked={reportDocumentIds.includes(d.id)}
                        onChange={() => setReportDocumentIds((prev) => toggleSelection(prev, d.id))}
                      />
                      <span>{d.originalName}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="v-modal-card__actions">
              <button type="button" className="v-btn v-btn--ghost" onClick={() => setReportModalOpen(false)} disabled={reportBusy}>
                Cancel
              </button>
              <button type="button" className="v-btn v-btn--primary" onClick={handleExportPdfReport} disabled={reportBusy}>
                {reportBusy ? 'Generating...' : 'Download PDF report'}
              </button>
            </div>
          </div>
        </div>
      )}
      {openImageDoc && imagePreviewUrls[openImageDoc.id] && (
        <div className="v-modal-backdrop" role="presentation" onClick={() => setOpenImageDocId(null)}>
          <div className="v-modal-card v-modal-card--image" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-card__image-head">
              <strong>{openImageDoc.originalName || 'Image preview'}</strong>
              <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={() => setOpenImageDocId(null)}>
                Close
              </button>
            </div>
            <img
              src={imagePreviewUrls[openImageDoc.id]}
              alt={openImageDoc.originalName || 'Image preview'}
              className="v-modal-card__image-preview"
            />
          </div>
        </div>
      )}
    </div>
  );
}
